import React, { createContext, useContext, useState, useRef, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm, SubmitHandler, FormProvider, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useSelector } from "react-redux";
import { useMutation, useQuery } from "@apollo/client";
import * as Sentry from "@sentry/react";
import * as Yup from "yup";
import dayjs from "dayjs";

import {
  MUTATION_SUBMIT_FR_TO_DOCUMENT_REVIEW,
  MUTATION_UPDATE_CARRIER_FUNDING_REQUEST
} from "features/main/HaulPay/Carrier/RequestPay/query";
import {
  QUERY_BROKER_FUNDING_REQUEST,
  QUERY_CARRIER_FUNDING_REQUEST,
  VALIDATE_FUNDING_REQUESTS
} from "features/main/HaulPay/Shared/EditFundingRequest/Manage/query";
import {
  FR_CONTRACT_TYPE,
  CARRIER_FR_CONTRACT_TYPE,
  FR_STATUS,
  PERCENTAGE_LIST,
  FR_LINE_TYPE_LIST
} from "constants/haulpay-broker";
import { CARRIER_PAYMENT_SPEED } from "constants/haulpay-carrier";
import { routes } from "constants/routes";
import { IEditFundingRequest } from "features/main/HaulPay/Shared/EditFundingRequest/Manage";
import { getApiErrorMessage } from "helpers/apiError";
import { renderApiErrorMessage } from "helpers/renderApiError";
import { showErrorMessage, showSuccessMessage, showWarningMessage } from "helpers/toastMessage";
import { useUserFeature } from "hooks/useUserFeature";
import { selectFactoringProfile } from "store/user/selector";
import {
  IFRAttachment,
  IFundingRequestLineItems,
  ILineList,
  IPaymentProfile
} from "models/HaulPay";
import {
  deleteFundingRequestAPI,
  sendInvoiceAPI,
  resendRateConfirmationAPI,
  calculateFR,
  declineSelfFinanceFRAPI,
  cancelCarrierPaymentAPI
} from "services/api/haulpay";
import formatBankAccount from "helpers/formatBankAccount";
import { formatNumber } from "helpers/formatNumber";
import { IOption } from "models/Form";
import { MUTATION_UPDATE_BROKER_FUNDING_REQUEST } from "features/main/Loads/Post/FundingRequest/query";
import { EQUIPMENT_TYPES } from "constants/signup";
import { UPLOAD_CATEGORY } from "constants/base";
import {
  FUNDING_REQUEST_CONTRACT_TYPES,
  FUNDING_REQUEST_CONTRACT_TYPES_MAP
} from "helpers/constantTypes";
import { useModal } from "context/ModalContext";
import { formatPhoneNumber } from "helpers/formatPhoneNumber";
import {
  canBrokerEditApprovedFactoredFR,
  shouldOmitStatusFromBrokerUpdate,
  isApprovedSelfFinanceOffPlatformEdit,
  getOffPlatformResendInvoicePrompt
} from "./permissions";
import { getPaymentSettingsConfirmationModalId } from "./paymentSettingsConfirmationRouting";
import { getInitialCarrierInvoiceAmount } from "./initialCarrierInvoiceAmount";
import { hasRequiredDocumentReviewDocuments } from "./documentReviewEligibility";
import {
  confirmContractTypeChangePaymentSettings,
  confirmSelfFinanceOffPlatformEdit,
  getPaymentSpeedSummary,
  LOCKED_BROKER_SPLIT_OPTION,
  type OffPlatformEditSummaryRow
} from "features/main/HaulPay/Shared/paymentSettingsConfirmationFlow";
import { confirmSelfFinanceNoInvoiceToNonFactored } from "features/main/HaulPay/Shared/selfFinanceNoInvoiceToNonFactoredFlow";
import { confirmSelfFinanceNoInvoiceToFactored } from "features/main/HaulPay/Shared/selfFinanceNoInvoiceToFactoredFlow";
import { confirmSelfFinanceNoInvoiceToSelfFinanceWithInvoice } from "features/main/HaulPay/Shared/selfFinanceNoInvoiceToSelfFinanceWithInvoiceFlow";
import { confirmNonFactoredToFactored } from "features/main/HaulPay/Shared/nonFactoredToFactoredFlow";
import { confirmNonFactoredBillOutToPayCarrier } from "features/main/HaulPay/Shared/nonFactoredBillOutToPayCarrierFlow";
import dollarsToCents from "helpers/dollarsToCents";

type FormValues = Partial<IEditFundingRequest>;

const NO_DEBTOR_ID = "debdebde-debd-debd-debd-111111111111";

const normalizeStatus = (status?: string): string =>
  String(status ?? "")
    .toLowerCase()
    .replace(/[\s_]/g, "");

const isHaulPayReviewStatus = (status?: string): boolean => {
  return normalizeStatus(status) === "haulpayreview";
};

const isDocsLockedStatus = (status?: string): boolean => {
  const normalized = normalizeStatus(status);
  return (
    normalized === "haulpayreview" || ["approved", "remoteapproved", "paid"].includes(normalized)
  );
};

// HP-8994: Document Review is intentionally absent. Submitting locks the FR against carrier edits.
const CARRIER_EDITABLE_STATUSES = ["Incomplete", "Document Issue", "Pending", "Fuel Advance"];
const BROKER_EDITABLE_STATUSES = [
  "Incomplete",
  "Document Issue",
  "Pending",
  "Hold",
  "Document Review"
];

const isFundingRequestDisabled = (status: string, lockedForReview: boolean, isCarrier: boolean) => {
  const editableStatuses = isCarrier ? CARRIER_EDITABLE_STATUSES : BROKER_EDITABLE_STATUSES;
  return lockedForReview || !editableStatuses.includes(status);
};

const getNumericPaymentSpeed = (payoutDays: unknown) => {
  if (!payoutDays) return 0;
  let value: string | number;
  if (typeof payoutDays === "string" || typeof payoutDays === "number") {
    value = payoutDays;
  } else if (
    typeof payoutDays === "object" &&
    "value" in payoutDays &&
    (typeof payoutDays.value === "string" || typeof payoutDays.value === "number")
  ) {
    value = payoutDays.value;
  } else {
    return 0;
  }

  const match = /\d+/.exec(String(value));
  return match ? Number.parseInt(match[0], 10) : 0;
};

const createValidationSchema = (
  allowNegativeMargin: boolean,
  negativeMarginValue: number,
  negativeMarginLimit: number,
  prevContractType: string,
  status?: string,
  isCarrier?: boolean,
  hasPaymentProfile?: boolean
) => {
  if (isCarrier && status) {
    return Yup.object<FormValues>({
      shipperInvoiceAmount: Yup.number()
        .typeError("This field is required")
        .required("This field is required"),
      debtorAdvanceAmount: Yup.number().min(0, "Must be greater than or equal to 0"),
      billToCompanyAddress: Yup.object().when([], {
        is: () => status !== "Incomplete",
        then: (schema) => schema.required("This field is required"),
        otherwise: (schema) => schema.notRequired()
      }),
      billToCompanyPhone: Yup.string().when([], {
        is: () => status !== "Incomplete",
        then: (schema) => schema.required("This field is required"),
        otherwise: (schema) => schema.notRequired()
      })
    });
  }

  return Yup.object<FormValues>({
    fundingRequestType: Yup.object().test(
      "block-contract-type-change",
      "Cannot change from self-finance to factored when negative margin is not allowed",
      function (value: IOption) {
        const { carrierInvoiceAmount, shipperInvoiceAmount } = this.parent as FormValues;

        const isChangingFromSelfFinance =
          prevContractType?.startsWith("SELF_FINANCE") && !value?.value?.startsWith("SELF_FINANCE");

        if (!isChangingFromSelfFinance) return true;

        const hasNegativeMargin =
          typeof carrierInvoiceAmount === "number" &&
          typeof shipperInvoiceAmount === "number" &&
          carrierInvoiceAmount > shipperInvoiceAmount;

        if (hasNegativeMargin && !allowNegativeMargin) {
          return this.createError({
            message:
              "Cannot change from self-finance to factored when negative margin is not allowed."
          });
        }
        return true;
      }
    ),

    debtor: Yup.object().nullable().notRequired(),

    shipperInvoiceAmount: Yup.number()
      .required("This field is required")
      .typeError("This field is required")
      .transform((val, orig) => (orig === "" ? undefined : val)),

    carrierInvoiceAmount: Yup.number()
      .required("This field is required")
      .typeError("This field is required")
      .test("carrier-invoice-amount", "", function (value) {
        const { shipperInvoiceAmount, fundingRequestType } = this.parent as FormValues;
        const isSelfFinance = fundingRequestType?.value?.startsWith?.("SELF_FINANCE");
        const isFactored = !fundingRequestType?.value?.startsWith?.("NON_FACTORED");
        const isOffPlatform =
          fundingRequestType?.value ===
          FUNDING_REQUEST_CONTRACT_TYPES.SELF_FINANCE_WITH_INVOICE_OFF_PLATFORM;
        if (typeof value !== "number" || typeof shipperInvoiceAmount !== "number") return true;

        if (isOffPlatform && value === 0) return true;

        if (
          isFactored &&
          value <= 0 &&
          hasPaymentProfile &&
          prevContractType !== "NON_FACTORED_BILL_OUT"
        ) {
          return this.createError({
            message: "Carrier payment must be greater than $0 for factored funding requests."
          });
        }

        if (value > shipperInvoiceAmount && !isSelfFinance && !allowNegativeMargin) {
          return this.createError({
            message: `Carrier invoice amount must be ≤ ${formatNumber(shipperInvoiceAmount, 2)}`
          });
        }
        return true;
      })
      .transform((val, orig) => (orig === "" ? undefined : val))
  });
};

interface FundingRequestFormContextType {
  methods: ReturnType<typeof useForm<FormValues>>;
  selectedFR: any;
  attachments: IFRAttachment[];
  isGlobalDisabled: boolean;
  allowDocsUpdate: boolean;
  isSubmitting: boolean;
  loading: boolean;
  refetch: () => Promise<void>;
  isLockedForReview: boolean;

  /** Mutual-exclusion: true when the FR type was changed (other fields should be disabled) */
  isTypeChanged: boolean;
  /** Mutual-exclusion: true when any non-type field was changed (type field should be disabled) */
  isOtherFieldsChanged: boolean;
  /** True when carrier-side fields that affect payout amounts or timing changed */
  isCarrierFieldsChanged: boolean;
  /** True when submitting now would trigger the off-platform edit confirmation (suppresses the
   *  separate carrier/shipper-edit confirmation so only one modal shows for the same edit) */
  isSelfFinanceOffPlatformEditPending: boolean;
  /** Convenience: should the FR type selector be disabled due to mutual-exclusion? */
  isTypeDisabled: boolean;
  /** Convenience: should non-type fields be disabled due to mutual-exclusion? */
  isOtherFieldsDisabled: boolean;
  /** Tooltip message shown on the type field when it is locked */
  typeDisabledTooltip: string;
  /** Tooltip message shown on other fields when they are locked */
  otherFieldsDisabledTooltip: string;

  /** Active tooltip ID - only one tooltip can be open at a time */
  activeTooltipId: string | null;
  /** Set which tooltip is currently active (open) */
  setActiveTooltipId: (id: string | null) => void;

  /** RHF handleSubmit (curried validator) */
  rhfHandleSubmit: (
    onValid: SubmitHandler<FormValues>
  ) => (e?: React.BaseSyntheticEvent) => Promise<void>;

  /** Your async API submitter (the one that actually calls mutations) */
  submitFR: (data: FormValues) => Promise<boolean>;

  /** HP-8994: explicit carrier submission of an Incomplete FR to Document Review */
  submitToDocumentReview: () => Promise<boolean>;
  /** HP-8994: true when the required labeled documents are present on an Incomplete carrier FR */
  canSubmitToDocumentReview: boolean;
  isFuelAdvance: boolean;
  /** HP-8994: true when the carrier can see the Save / Mark as Complete pair on this FR */
  showCarrierSubmitActions: boolean;
  /** HP-8994: true once the carrier has marked the FR complete and it sits in Document Review */
  isMarkedCompleteState: boolean;
  isSubmittingToDocumentReview: boolean;

  sendInvoice: () => Promise<void>;
  /** HP-8868: resolves false when the resend failed, so the modal can stay open */
  resendRateConfirmation: () => Promise<boolean>;
  deleteFR: () => Promise<void>;
  declineFR: () => Promise<void>;
  cancelCarrierPayment: () => Promise<void>;
  handleAttachmentsChange: (newAttachments: IFRAttachment[]) => void;
  isMarkedComplete: boolean;
  hasBOLDocument: boolean;
  isAssignedLoadsPage: boolean;

  brokerGrossMargin: number;
  reserveHoldBack: number;
  factoringFee: number;
  factoringPayable: number;
  isFetchingCalculation: boolean;
  brokersQPFee: string;
  brokerPay: number;
  carrierRate: string;
  carrierFee: number;
  yourFee: number;
  carrierPay: number;
  selectedPaymentProfile: IPaymentProfile | undefined;
  setSelectedPaymentProfile: (profile: IPaymentProfile | undefined) => void;
  setIsAttachmentsListIsModalOpen: (isOpen: boolean) => void;
  isAttachmentListModalOpen: boolean;
  editingAttachment: IFRAttachment | null;
  setEditingAttachment: (attachment: IFRAttachment | null) => void;
  formMethods: ReturnType<typeof useForm<FormValues>>;
  selfFinanceFee: number;
  selfFinanceFeeRateInfo: string;
  brokerCarrierAdvance: number;
  selfFinanceTotalAmount: number;
  brokerQPProfit: number;
  qpFees: number;
  hasFactoringCompany: boolean;
  debtorAdvanceAmount: number;
  debtorAdditionalDiscountRate: number;
  brokerAdditionalDiscountFeeForCarrier: number;
  brokerAdditionalDiscountFeeForBroker: number;
  marginAdvanceFee: number;
  marginAdvanceAmount: number;
  brokerAdvanceFeePercent: number;
  brokerDiscountFeeForCarrier: number;
}

export const FundingRequestFormContext = createContext<FundingRequestFormContextType | null>(null);
export type { FundingRequestFormContextType };

export function FundingRequestFormProvider({
  children,
  isAssignedLoad
}: {
  children: React.ReactNode;
  isAssignedLoad?: boolean;
}) {
  const { fundingRequestId } = useParams();
  const navigate = useNavigate();
  const { isBroker, isCarrier, isNonFactorRefactorEnabled } = useUserFeature();
  const isAssignedLoadsPage = isAssignedLoad ?? false;
  const factoringProfile = useSelector(selectFactoringProfile);
  const { openModal, closeModal } = useModal();

  const allowNegativeMargin = factoringProfile?.allow_negative_margin ?? false;
  const negativeMarginValue = (factoringProfile?.negative_margin_value ?? 0) / 100;
  const negativeMarginLimit = factoringProfile?.negative_margin_limit ?? 0;

  const [selectedFR, setSelectedFR] = useState<any>();
  const [attachments, setAttachments] = useState<IFRAttachment[]>([]);
  const [isGlobalDisabled, setIsGlobalDisabled] = useState(false);
  const [allowDocsUpdate, setAllowDocsUpdate] = useState(false);
  const [isLockedForReview, setIsLockedForReview] = useState(false);
  const [selectedPaymentProfile, setSelectedPaymentProfileState] = useState<IPaymentProfile>();
  const [hasBOLDocument, setHasBOLDocument] = useState(false);
  const [isMarkedComplete, setIsMarkedComplete] = useState<boolean>(false);
  const [autoInvoice, setAutoInvoice] = useState<boolean>(false);
  const [isSubmittingToDocumentReview, setIsSubmittingToDocumentReview] = useState(false);
  const [isFetchingCalculation, setFetchingCalculation] = useState<boolean>(false);
  const [calculationResults, setCalculationResults] = useState<any>(null);
  const initialValuesRef = useRef<FormValues | null>(null);
  const lastCalculatedFundingRequestIdRef = useRef<string>();
  const calculationRequestIdRef = useRef(0);
  const [isAttachmentListModalOpen, setIsAttachmentsListIsModalOpen] = useState(false);
  const [editingAttachment, setEditingAttachment] = useState<IFRAttachment | null>(null);
  const [hasFactoringCompany, setHasFactoringCompany] = useState<boolean>(false);
  const paymentSettingsConfirmationAbortControllerRef = useRef<AbortController | null>(null);

  useEffect(
    () => () => {
      paymentSettingsConfirmationAbortControllerRef.current?.abort();
    },
    []
  );

  // ── Mutual-exclusion: type change vs other-field changes ──
  const [isTypeChanged, setIsTypeChanged] = useState(false);
  const [isOtherFieldsChanged, setIsOtherFieldsChanged] = useState(false);
  const [isCarrierFieldsChanged, setIsCarrierFieldsChanged] = useState(false);
  const [isSelfFinanceOffPlatformEditPending, setIsSelfFinanceOffPlatformEditPending] =
    useState(false);

  // ── Tooltip state management: only one tooltip open at a time ──
  const [activeTooltipId, setActiveTooltipId] = useState<string | null>(null);

  const isTypeDisabled = isOtherFieldsChanged;
  const isOtherFieldsDisabled = isTypeChanged;

  const typeDisabledTooltip =
    "Save or revert your current field changes before changing the funding request type.";
  const otherFieldsDisabledTooltip =
    "Save or revert the funding request type change before editing other fields.";

  const setSelectedPaymentProfile = (profile: IPaymentProfile | undefined) => {
    setSelectedPaymentProfileState(profile);

    if (!profile) {
      setHasFactoringCompany(false);
      return;
    }

    const hasFactoring =
      Boolean((profile as any)?.factoring_company?.name) ||
      Boolean(
        (profile as any)?.brokerPaymentProfileRelation?.factoringCompanyRelation?.factoringCompany
          ?.id
      );

    setHasFactoringCompany(hasFactoring);
  };

  const validationSchema = useMemo(
    () =>
      createValidationSchema(
        allowNegativeMargin,
        negativeMarginValue,
        negativeMarginLimit,
        selectedFR?.contractType ?? "",
        selectedFR?.status,
        isCarrier,
        !!(selectedFR?.paymentProfile || selectedPaymentProfile)
      ),
    [
      allowNegativeMargin,
      negativeMarginValue,
      negativeMarginLimit,
      selectedFR?.contractType,
      selectedFR?.status,
      isCarrier,
      selectedFR?.paymentProfile,
      selectedPaymentProfile
    ]
  );

  const methods = useForm<FormValues>({
    mode: "all",
    reValidateMode: "onBlur",
    resolver: yupResolver(validationSchema)
  });

  const {
    reset,
    setValue,
    watch,
    control,
    formState: { isSubmitting }
  } = methods;

  // Fetch FR
  const {
    data,
    loading,
    error,
    refetch: refetchQuery
  } = useQuery(isBroker ? QUERY_BROKER_FUNDING_REQUEST : QUERY_CARRIER_FUNDING_REQUEST, {
    variables: { id: fundingRequestId, assignedLoad: isAssignedLoadsPage },
    fetchPolicy: "cache-and-network"
  });

  // Runs the calculation straight off the fetched funding request, bypassing the
  // debounced effect below. The debounce is driven by watched form values, which
  // change while the form is being reset from the fetched data, so on first load
  // its pending call is cancelled and never rescheduled.
  const runCalculationForFundingRequest = async (frData: any): Promise<void> => {
    if (!frData?.debtor?.id) return;

    const requestId = ++calculationRequestIdRef.current;
    setFetchingCalculation(true);
    try {
      const result = await calculateFR({
        debtor: frData.debtor.id,
        factored: !frData.contractType?.startsWith("NON_FACTORED"),
        invoice_amount: dollarsToCents(frData.amount),
        bill_out_only: frData.contractType === "NON_FACTORED_BILL_OUT",
        contract_type: frData.contractType,
        funding_request_id: frData.id,
        discount_rate_comfreight: frData.discountRate,
        ...(isBroker || isAssignedLoadsPage
          ? {
              apply_reserve_holdback: frData.applyReserveHoldback,
              factoring: frData.factoring?.id,
              create_rates_revision: false,
              factoring_fee_split_rate: Number(frData.factoringFeeSplitRate),
              self_finance_requested: frData.contractType?.startsWith("SELF_FINANCE"),
              payment_profile: frData.paymentProfile?.id,
              payment_profile_amount: 0,
              payment_profile_invoice_amount: dollarsToCents(frData.paymentProfileAmount),
              payment_speed: getNumericPaymentSpeed(frData.payoutDays)
            }
          : {})
      });
      if (requestId !== calculationRequestIdRef.current) return;
      if (result?.data) {
        setCalculationResults(result.data);
      }
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      if (requestId === calculationRequestIdRef.current) {
        setFetchingCalculation(false);
      }
    }
  };

  // Add refetch function to refetch the funding request data
  const refetchFundingRequest = async (): Promise<boolean> => {
    try {
      const res = await refetchQuery();
      const frData = res && !res.loading ? res.data?.fundingRequest : undefined;
      if (!frData) {
        return true;
      }

      setSelectedFR(frData);
      const lockedForReview = isHaulPayReviewStatus(frData.status);
      const docsLocked = isDocsLockedStatus(frData.status);
      const attachments = frData.attachments || [];
      setIsLockedForReview(lockedForReview);
      setAttachments(attachments);
      setHasBOLDocument(checkForBOLDocument(attachments));
      setAllowDocsUpdate(
        !docsLocked && ["Incomplete", "Document Issue", "Fuel Advance"].includes(frData.status)
      );
      setIsGlobalDisabled(isFundingRequestDisabled(frData.status, lockedForReview, isCarrier));

      await runCalculationForFundingRequest(frData);
      return true;
    } catch (error) {
      showErrorMessage("Failed to refresh funding request data");
      return false;
    }
  };

  const refetch = async (): Promise<void> => {
    await refetchFundingRequest();
  };

  const mapCategoryStringToOption = (category: string): IOption | null => {
    // First try direct case-insensitive match
    const directMatch = FR_LINE_TYPE_LIST.find(
      (option) => option.value.toLowerCase() === category.toLowerCase()
    );
    if (directMatch) return directMatch;

    // Try to find a match by replacing underscores with spaces and comparing
    const normalizedCategory = category.toLowerCase().replace(/_/g, " ");
    return (
      FR_LINE_TYPE_LIST.find(
        (option) => option.value.toLowerCase().replace(/_/g, " ") === normalizedCategory
      ) || null
    );
  };

  useEffect(() => {
    if (!loading && error) showErrorMessage(error.message);
  }, [loading, error]);

  useEffect(() => {
    if (!data) return;
    const fr = data.fundingRequest;
    const lockedForReview = isHaulPayReviewStatus(fr.status);
    const docsLocked = isDocsLockedStatus(fr.status);
    setIsLockedForReview(lockedForReview);
    setSelectedFR(fr);
    setIsGlobalDisabled(isFundingRequestDisabled(fr.status, lockedForReview, isCarrier));

    // Set allowDocsUpdate to only allow document upload/edit for these specific statuses
    setAllowDocsUpdate(
      !docsLocked && ["Incomplete", "Document Issue", "Fuel Advance"].includes(fr.status)
    );

    const attachments = fr.attachments || [];
    setAttachments(attachments);
    setHasBOLDocument(checkForBOLDocument(attachments));

    setIsMarkedComplete(fr.metadata?.marked_as_completed ?? false);
    setAutoInvoice(fr?.autoInvoice ?? false);

    if (fr.paymentProfile) setSelectedPaymentProfile(fr.paymentProfile);

    const frId = String(fr.id ?? "");
    if (frId && lastCalculatedFundingRequestIdRef.current !== frId) {
      lastCalculatedFundingRequestIdRef.current = frId;
      runCalculationForFundingRequest(fr);
    }
  }, [data, isCarrier]);

  // Initialize form values
  useEffect(() => {
    if (!selectedFR) return;
    const fundingRequestTypeObject =
      (isCarrier ? CARRIER_FR_CONTRACT_TYPE : FR_CONTRACT_TYPE).find(
        ({ value }) => selectedFR?.contractType === value
      ) || FR_CONTRACT_TYPE[0];

    const brokersQRFeeObject =
      PERCENTAGE_LIST.find(
        ({ value }) => value === selectedFR?.factoringFeeSplitRate?.split(".")[0]
      ) || PERCENTAGE_LIST[0];

    const carrierPaymentSpeedObject =
      CARRIER_PAYMENT_SPEED.find(({ value }) => value === selectedFR?.payoutDays) ||
      CARRIER_PAYMENT_SPEED[0];
    const equipmentType =
      EQUIPMENT_TYPES.find((item) => item.value === selectedFR?.LoadTrailerType) ||
      EQUIPMENT_TYPES[0];

    // Check if factoring company is assigned to THIS broker relationship
    const relationFactoringCompany =
      selectedFR?.paymentProfile?.brokerPaymentProfileRelation?.factoringCompanyRelation
        ?.factoringCompany;

    const paymentProfileMethod = relationFactoringCompany
      ? selectedFR?.paymentProfile?.brokerPaymentProfileRelation?.factoringCompanyRelation
          ?.factoringCompany?.account?.method
      : selectedFR?.paymentMethod;

    const carrierPaymentMethod = relationFactoringCompany
      ? `${selectedFR?.paymentProfile?.brokerPaymentProfileRelation?.factoringCompanyRelation?.factoringCompany?.name?.toUpperCase()} ${formatBankAccount(
          selectedFR?.paymentProfile?.brokerPaymentProfileRelation?.factoringCompanyRelation
            ?.factoringCompany?.account?.accountNumberRedacted,
          selectedFR?.paymentProfile?.brokerPaymentProfileRelation?.factoringCompanyRelation
            ?.factoringCompany?.account?.method
        )}`
      : formatBankAccount(
          selectedFR?.paymentProfileAccount?.accountNumberRedacted,
          selectedFR?.paymentProfileAccount?.method
        );
    setHasFactoringCompany(!!relationFactoringCompany?.id);
    const lineItems = (selectedFR?.frLineItems?.map((item: ILineList) => {
      const mappedCategory = mapCategoryStringToOption(item.category);
      if (!mappedCategory) {
        return {
          id: item.id,
          amount: item.amount / 100,
          category: { id: 999, label: "Unknown", value: "Unknown" },
          description: item.description
        };
      }
      return {
        id: item.id,
        amount: item.amount / 100,
        category: mappedCategory,
        description: item?.description
      };
    }) || []) as IFundingRequestLineItems[];
    const companyProfile = JSON.parse(selectedFR.factoring?.companyProfile ?? "{}");
    const ownerProfile = JSON.parse(selectedFR.factoring?.ownerProfile ?? "{}");
    const brokerAddress = companyProfile?.address ?? "";
    const formattedPhone = formatPhoneNumber(ownerProfile?.phone_number_cell) ?? "";
    const brokerPhoneNumber = formattedPhone.length === 12 ? "1" + formattedPhone : formattedPhone;

    const getPaymentMethod = (paymentMethod: any, factoringCompany: any) => {
      return factoringCompany?.name ? factoringCompany?.account?.method : paymentMethod?.method;
    };

    const initialValues: FormValues = {
      invoiceNumber: selectedFR?.invoiceNumber,
      userLoadNumber: selectedFR?.userLoadNumber,
      fundingRequestType: fundingRequestTypeObject,
      lineItems,
      shipperInvoiceAmount:
        parseFloat(selectedFR?.displayAmount?.toFixed(2)) ||
        parseFloat(selectedFR?.amount?.toFixed(2)),
      carrierInvoiceAmount: getInitialCarrierInvoiceAmount({
        isBroker,
        isAssignedLoadsPage,
        paymentProfileAmount: selectedFR?.paymentProfileAmount,
        displayAmount: selectedFR?.displayAmount,
        amount: selectedFR?.amount
      }),
      brokerGrossMargin: selectedFR?.brokerGrossMargin || 0,
      reserveHoldBack: selectedFR?.reserveHoldBack || 0,
      yourFee: selectedFR?.factoringFee || 0,
      brokerPay: selectedFR?.factoringPayable || 0,
      carrierPay: selectedFR?.paymentProfilePayable || selectedFR?.paymentProfileAmount || 0,
      paymentProfileDisplay: getPaymentMethod(
        selectedFR.paymentProfileAccount,
        selectedFR.factoringCompany
      ),
      paymentProfile: selectedFR?.paymentProfile?.companyName,
      carrierMc: selectedFR?.paymentProfile?.companyMc,
      carrierDot: selectedFR?.paymentProfile?.companyDot,
      carrierPhone: selectedFR?.paymentProfile?.companyPhoneNumber,
      carrierEmail: selectedFR?.paymentProfile?.ownerEmail,
      carrierPaymentMethod,
      paymentProfileMethod,
      carrierPaymentSpeed: carrierPaymentSpeedObject,
      carrierPro: selectedFR?.carrierProNumber,
      factoringCompanyName:
        selectedFR?.paymentProfile?.brokerPaymentProfileRelation?.factoringCompanyRelation?.factoringCompany?.name?.toUpperCase() ||
        "",
      debtor: isAssignedLoadsPage ? companyProfile : selectedFR?.debtor,
      billToCompanyAddress: isAssignedLoadsPage ? brokerAddress : selectedFR.billToAddress,
      billToCompanyPhone: isAssignedLoadsPage ? brokerPhoneNumber : selectedFR.billToCompanyPhone,
      debtorInvoiceDetails: selectedFR?.debtorInvoiceNotes,

      brokersQRFee: brokersQRFeeObject,
      PONumber: selectedFR?.purchaseOrderNumber,
      clientInvoiceNumber: selectedFR?.userReferenceNumber,
      equipmentType,
      statusNote: selectedFR?.statusNote,
      carrierInvoiceDetails: selectedFR?.carrierInvoiceNotes,
      marginAdvance: selectedFR?.applyReserveHoldback,
      debtorAdvanceAmount: parseFloat((Number(selectedFR?.debtorAdvance ?? 0) / 100).toFixed(2)),
      userNotes: selectedFR?.userNotes,

      firstPickUp: selectedFR?.firstOriginLocation,
      finalDelivery: selectedFR?.finalDestinationLocation,

      createdAt: selectedFR?.created ? dayjs(selectedFR?.created).format("MM/DD/YYYY") : "",
      invoiceDate: selectedFR?.invoiceDate
        ? dayjs(selectedFR?.invoiceDate).format("MM/DD/YYYY")
        : "",

      fundingRequestStatus:
        FR_STATUS.find(
          ({ value }) => value === selectedFR?.contractType && selectedFR?.status === "Non Factored"
        ) ?? FR_STATUS.find(({ label }) => label === selectedFR?.status),

      attachments: selectedFR?.attachments || [],
      factored: selectedFR?.factored
    };

    initialValuesRef.current = initialValues;
    reset(initialValues);
    // Reset mutual-exclusion flags when initial values are (re)loaded
    setIsTypeChanged(false);
    setIsOtherFieldsChanged(false);
  }, [selectedFR, isBroker, isCarrier]);

  // ── Mutual-exclusion watcher ──
  // Compare current form values to initial values to determine which "bucket" changed
  const otherFieldKeys: (keyof FormValues)[] = [
    "lineItems",
    "invoiceNumber",
    "userLoadNumber",
    "shipperInvoiceAmount",
    "paymentProfile",
    "carrierInvoiceAmount",
    "debtor",
    "fundingRequestStatus",
    "PONumber",
    "clientInvoiceNumber",
    "debtorInvoiceDetails",
    "userNotes",
    "statusNote",
    "carrierPro",
    "carrierInvoiceDetails",
    "carrierPaymentSpeed",
    "brokersQRFee",
    "debtorAdvanceAmount",
    "firstPickUp",
    "finalDelivery",
    "billToCompanyAddress",
    "billToCompanyPhone",
    "marginAdvance"
  ];

  // Watch only the specific fields needed for mutual-exclusion logic
  const watchedFields = useWatch({
    control,
    name: ["fundingRequestType", ...otherFieldKeys] as const
  });

  useEffect(() => {
    const initial = initialValuesRef.current;
    if (!initial) return;

    // watchedFields is an array in the same order as the name array
    const [fundingRequestType, ...otherFieldValues] = watchedFields;

    const hasFieldChanged = (key: keyof FormValues, current: unknown) => {
      const orig = initial[key];

      // For object fields (selects, debtor, address), compare by value/id
      if (current && typeof current === "object" && orig && typeof orig === "object") {
        const cv = (current as any).value ?? (current as any).id;
        const ov = (orig as any).value ?? (orig as any).id;
        if (cv !== undefined || ov !== undefined) return cv !== ov;
        return JSON.stringify(current) !== JSON.stringify(orig);
      }

      // Primitive comparison (handle undefined/null/empty coalescing)
      return String(current ?? "") !== String(orig ?? "");
    };

    // Check if funding request type changed
    const currentTypeValue = (fundingRequestType as any)?.value;
    const initialTypeValue = initial.fundingRequestType?.value;
    const typeHasChanged = currentTypeValue !== initialTypeValue;

    // Check if any other editable field changed (compare key fields that users can modify)
    const anyOtherChanged = otherFieldKeys.some((key, index) =>
      hasFieldChanged(key, otherFieldValues[index])
    );
    const carrierFieldKeys: (keyof FormValues)[] = [
      "paymentProfile",
      "carrierInvoiceAmount",
      "carrierPaymentSpeed",
      "brokersQRFee"
    ];
    const carrierFieldsChanged = carrierFieldKeys.some((key) => {
      const index = otherFieldKeys.indexOf(key);
      return index >= 0 && hasFieldChanged(key, otherFieldValues[index]);
    });

    // HP-9100: mirrors submitFR's own off-platform-edit check below (same field set, same
    // hasFieldChanged comparison) so this pre-submit preview can never drift from what submitFR
    // actually decides at submit time.
    const changed = (key: keyof FormValues) =>
      hasFieldChanged(key, otherFieldValues[otherFieldKeys.indexOf(key)]);
    const offPlatformEditPending = isApprovedSelfFinanceOffPlatformEdit({
      contractType: selectedFR?.contractType,
      status: selectedFR?.status,
      carrierAmountChanged: changed("carrierInvoiceAmount"),
      shipperAmountChanged: changed("shipperInvoiceAmount"),
      payoutDaysChanged: changed("carrierPaymentSpeed"),
      brokerSplitChanged: changed("brokersQRFee")
    });

    setIsTypeChanged(typeHasChanged);
    setIsOtherFieldsChanged(anyOtherChanged);
    setIsCarrierFieldsChanged(carrierFieldsChanged);
    setIsSelfFinanceOffPlatformEditPending(offPlatformEditPending);
  }, [watchedFields]);

  // Use refs to track and prevent duplicate calculations
  const calculationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const previousCalculationParamsRef = useRef<string>("");

  // Memoize calculated values to prevent unnecessary recalculations
  const calculatedValues = useMemo(() => {
    return {
      brokerGrossMargin: parseFloat(
        Math.floor(
          calculationResults?.invoice_amount - calculationResults?.payment_profile_invoice_amount
        ).toFixed(2)
      ),
      reserveHoldBack: parseFloat(Math.floor(calculationResults?.reserve_amount).toFixed(2)),
      factoringFee: parseFloat(Math.floor(calculationResults?.factoring_fee).toFixed(2)),
      factoringPayable: parseFloat(Math.floor(calculationResults?.factoring_payable).toFixed(2)),
      carrierPay: parseFloat(
        Math.floor(
          selectedFR?.contractType?.startsWith("NON_FACTORED")
            ? calculationResults?.payment_profile_invoice_amount
            : calculationResults?.payment_profile_payable ?? 0
        ).toFixed(2)
      ),
      yourFee: parseFloat(Math.floor(calculationResults?.factoring_fee).toFixed(2)),
      brokerPay: parseFloat(Math.floor(calculationResults?.factoring_payable).toFixed(2)),
      carrierFee: parseFloat(Math.floor(calculationResults?.payment_profile_fee ?? 0).toFixed(2)),
      ...(isBroker && { carrierRate: calculationResults?.payment_profile_discount_rate + "%" }),
      brokersQPFee:
        PERCENTAGE_LIST.find(
          ({ amount }) => amount === calculationResults?.factoring_fee_split_rate
        )?.label || PERCENTAGE_LIST[0].label,
      selfFinanceFee: parseFloat(
        Math.floor(calculationResults?.broker_self_finance_fee).toFixed(2)
      ),
      selfFinanceFeeRateInfo: calculationResults?.self_finance_fee_rate_info,
      brokerCarrierAdvance: parseFloat(
        Math.floor(calculationResults?.broker_carrier_advance).toFixed(2)
      ),
      selfFinanceTotalAmount: parseFloat(
        Math.floor(calculationResults?.self_finance_total_amount).toFixed(2)
      ),
      qpFees: calculationResults?.qp_total_fee,
      brokerQPProfit: parseFloat(Math.floor(calculationResults?.broker_qp_profit).toFixed(2)),
      debtorAdvanceAmount: parseFloat(
        Math.floor(calculationResults?.debtor_advance_amount).toFixed(2)
      ),
      debtorAdditionalDiscountRate: calculationResults?.debtor_additional_discount_rate ?? 0,
      brokerAdditionalDiscountFeeForCarrier: parseFloat(
        Math.floor(calculationResults?.broker_additional_discount_fee_for_carrier ?? 0).toFixed(2)
      ),
      brokerAdditionalDiscountFeeForBroker: parseFloat(
        Math.floor(calculationResults?.broker_additional_discount_fee_for_broker ?? 0).toFixed(2)
      ),
      marginAdvanceFee: parseFloat(
        Math.floor(calculationResults?.margin_advance_fee ?? 0).toFixed(2)
      ),
      marginAdvanceAmount: parseFloat(
        Math.floor(calculationResults?.net_margin_advance_amount ?? 0).toFixed(2)
      ),
      brokerAdvanceFeePercent: calculationResults?.factoring_margin_advance_discount_rate
        ? parseFloat(calculationResults.factoring_margin_advance_discount_rate.toFixed(2))
        : 0,
      brokerDiscountFeeForCarrier: parseFloat(
        Math.floor(calculationResults?.broker_discount_fee_for_carrier ?? 0).toFixed(2)
      )
    };
  }, [calculationResults, isBroker, isAssignedLoadsPage, selectedFR?.contractType]);

  // Destructure the merged values
  const {
    brokerGrossMargin,
    reserveHoldBack,
    factoringFee,
    factoringPayable,
    brokersQPFee,
    brokerPay,
    carrierRate,
    carrierFee,
    yourFee,
    carrierPay,
    selfFinanceFee,
    selfFinanceFeeRateInfo,
    brokerCarrierAdvance,
    selfFinanceTotalAmount,
    brokerQPProfit,
    qpFees,
    debtorAdvanceAmount,
    debtorAdditionalDiscountRate,
    brokerAdditionalDiscountFeeForCarrier,
    brokerAdditionalDiscountFeeForBroker,
    marginAdvanceFee,
    marginAdvanceAmount,
    brokerAdvanceFeePercent,
    brokerDiscountFeeForCarrier
  } = calculatedValues;

  // Watched values that affect fee calculations
  const watchedContractType = watch("fundingRequestType");
  const watchedShipperAmount = watch("shipperInvoiceAmount");
  const watchedCarrierAmount = watch("carrierInvoiceAmount");
  const watchedPayoutDays = watch("carrierPaymentSpeed");
  const watchedQrFee = watch("brokersQRFee");
  const watchedMarginAdvance = watch("marginAdvance");

  // API-based calculation with debouncing
  useEffect(() => {
    if (!selectedFR?.debtor?.id) return;

    const contractTypeValue = watchedContractType?.value || "";
    const shipperAmount = watchedShipperAmount || 0;
    // HP-9210: deliberately NOT converted here. Both consumers below call dollarsToCents on it,
    // so converting on the assigned-loads page sent the calculation a hundred times the amount.
    const carrierAmount = watchedCarrierAmount || 0;

    // Get payment speed value
    const payoutDays = watchedPayoutDays;
    let paymentSpeed = 0;

    if (payoutDays) {
      // Extract numeric value from payment speed (e.g., "2-3" becomes 2)
      const match = String(typeof payoutDays === "object" ? payoutDays.value : payoutDays).match(
        /\d+/g
      );
      if (match && match.length > 0) {
        paymentSpeed = parseInt(match[0], 10);
      }
    }

    // Get QR fee percentage
    const qrFee = watchedQrFee;
    const feePercentage =
      typeof qrFee === "object" ? parseFloat(qrFee?.value || "2.5") : parseFloat(qrFee || "2.5");

    // Create a params object to compare with previous calculation
    const calculationParams = {
      debtor: selectedFR?.debtor?.id,
      invoice_amount: dollarsToCents(shipperAmount),
      payment_profile_invoice_amount: dollarsToCents(carrierAmount),
      contract_type: contractTypeValue,
      payment_speed: paymentSpeed,
      factoring_fee_split_rate: Number(feePercentage),
      payment_profile: selectedFR?.paymentProfile?.id,
      apply_reserve_holdback: Boolean(watchedMarginAdvance)
    };

    // Create a string representation of params for comparison
    const paramsString = JSON.stringify(calculationParams);

    // Check if we're trying to calculate with the same params as before
    if (paramsString === previousCalculationParamsRef.current) {
      return; // Skip duplicate calculation with same parameters
    }

    // Clear any existing timeout
    if (calculationTimeoutRef.current) {
      clearTimeout(calculationTimeoutRef.current);
    }

    // Set a new timeout (debounce for 500ms)
    calculationTimeoutRef.current = setTimeout(async () => {
      const requestId = ++calculationRequestIdRef.current;
      try {
        setFetchingCalculation(true);
        // Only call API if we have necessary values
        if (selectedFR?.debtor?.id) {
          const result = await calculateFR({
            debtor: selectedFR?.debtor?.id,
            factored: !contractTypeValue.startsWith("NON_FACTORED"),
            invoice_amount: dollarsToCents(shipperAmount),
            bill_out_only: contractTypeValue === "NON_FACTORED_BILL_OUT",
            funding_request_id: selectedFR?.id,
            contract_type: contractTypeValue,
            discount_rate_comfreight: selectedFR?.discountRate,
            ...(isBroker || isAssignedLoadsPage
              ? {
                  apply_reserve_holdback: Boolean(watchedMarginAdvance),
                  factoring: selectedFR?.factoring?.id,
                  create_rates_revision: false,
                  factoring_fee_split_rate: Number(feePercentage),
                  self_finance_requested: contractTypeValue.startsWith("SELF_FINANCE"),
                  payment_profile: selectedFR?.paymentProfile?.id,
                  payment_profile_amount: 0,
                  payment_profile_invoice_amount: dollarsToCents(carrierAmount),
                  payment_speed: paymentSpeed
                }
              : {})
          });

          // Ignore a response that a newer calculation has already superseded,
          // so a slower earlier request cannot overwrite fresher numbers.
          if (requestId !== calculationRequestIdRef.current) return;

          // Store calculation results
          if (result?.data) {
            setCalculationResults(result.data);
            // Recorded only after a successful call so a cancelled or failed
            // attempt is retried instead of being treated as already done.
            previousCalculationParamsRef.current = paramsString;
          }
        }
      } catch (error) {
        Sentry.captureException(error);
      } finally {
        if (requestId === calculationRequestIdRef.current) {
          setFetchingCalculation(false);
        }
      }
    }, 500);

    // Cleanup function
    return () => {
      if (calculationTimeoutRef.current) {
        clearTimeout(calculationTimeoutRef.current);
      }
    };
  }, [
    selectedFR?.debtor?.id,
    selectedFR?.paymentProfile?.id,
    watchedContractType,
    watchedShipperAmount,
    watchedCarrierAmount,
    watchedPayoutDays,
    watchedQrFee,
    watchedMarginAdvance
  ]);

  // Mutations
  const [updateBrokerFR] = useMutation(MUTATION_UPDATE_BROKER_FUNDING_REQUEST, {
    onError(err) {
      showErrorMessage(renderApiErrorMessage(err));
    }
  });

  const [updateCarrierFR] = useMutation(MUTATION_UPDATE_CARRIER_FUNDING_REQUEST, {
    onError(err) {
      showErrorMessage(renderApiErrorMessage(err));
    }
  });

  // Apollo resolves instead of rejecting when onError is supplied, so this records whether the
  // reviewer-facing message was already shown and submitToDocumentReview does not repeat it.
  const documentReviewErrorShownRef = useRef(false);
  const [submitFRToDocumentReview] = useMutation(MUTATION_SUBMIT_FR_TO_DOCUMENT_REVIEW, {
    onError(err) {
      documentReviewErrorShownRef.current = true;
      showErrorMessage(getApiErrorMessage(err));
    }
  });

  // Actions
  const sendInvoice = async () => {
    try {
      const res = await sendInvoiceAPI(fundingRequestId as any);
      if (res.data?.email_not_verified) {
        showWarningMessage(
          "This debtor's email has not been verified yet so an invoice cannot be sent at this time. We will verify it in 1-2 business days. If your debtor is set to receive invoices from us automatically then they will still receive their invoice once the funding request is approved even if you don't come back to send it from here later."
        );
      } else {
        showSuccessMessage("Invoice sent successfully.");
      }
      await refetch();
    } catch {
      showErrorMessage("Failed to send invoice.");
    }
  };

  // HP-8868: the client's send_rate_confirmation_remote_approved setting can hold the email back,
  // so a 200 does not always mean it went out. Report the suppressed case instead of a false success.
  const resendRateConfirmation = async (): Promise<boolean> => {
    try {
      const res = await resendRateConfirmationAPI(fundingRequestId as any);
      if (res.data?.suppressed) {
        showWarningMessage(res.data.suppressed);
      } else {
        showSuccessMessage("Rate confirmation resent to the carrier.");
      }
      return true;
    } catch (error) {
      showErrorMessage(getApiErrorMessage(error));
      return false;
    }
  };

  const deleteFR = async () => {
    try {
      await deleteFundingRequestAPI(fundingRequestId as any);
      showSuccessMessage("Funding request deleted successfully.");
      navigate(isBroker ? routes.HAULPAY_BROKER_MANAGE_PAY : routes.HAULPAY_CARRIER_MANAGE_PAY);
    } catch {
      showErrorMessage("Failed to delete funding request.");
    }
  };

  const declineFR = async () => {
    try {
      await declineSelfFinanceFRAPI(fundingRequestId as any);
      showSuccessMessage("Funding request declined and carrier payment cancelled.");
      await refetch();
    } catch (err: any) {
      const detail = err?.response?.data?.funding_request;
      showErrorMessage(
        Array.isArray(detail) ? detail.join(" ") : detail || "Failed to decline funding request."
      );
      throw err;
    }
  };

  const cancelCarrierPayment = async () => {
    try {
      await cancelCarrierPaymentAPI(fundingRequestId as any);
      showSuccessMessage("Carrier payment cancelled. The invoice remains open for you to collect.");
      await refetch();
    } catch (err: any) {
      const detail = err?.response?.data?.funding_request;
      showErrorMessage(
        Array.isArray(detail) ? detail.join(" ") : detail || "Failed to cancel the carrier payment."
      );
      throw err;
    }
  };

  const [validateFRs] = useMutation(VALIDATE_FUNDING_REQUESTS, {
    onError: (err) => {
      if (Array.isArray(err))
        return err.forEach((message) =>
          showErrorMessage(renderApiErrorMessage(message), {
            autoClose: 100000
          })
        );
      return showErrorMessage(getApiErrorMessage(err), {
        autoClose: 100000
      });
    },
    onCompleted: (data) => {
      const hasErrors = data.validateFundingRequests.results.some((r: any) => !r?.valid);
      if (hasErrors) {
        throw data.validateFundingRequests.results
          .filter((r: any) => !r?.valid)
          .flatMap((r: any) => r.errors.map((err: string) => err));
      }
    }
  });

  // ===== Your actual submitter (renamed) =====
  const submitFR = async (data: FormValues): Promise<boolean> => {
    try {
      let res;
      let markedAsCompleted = false;
      let resendInvoicePrompt: string | null = null;

      const originLocations = {
        streetOne: data?.firstPickUp?.address ?? data?.firstPickUp?.street_one,
        city: data?.firstPickUp?.city ?? "",
        state: data?.firstPickUp?.state ?? "",
        zip: data?.firstPickUp?.zip ?? "",
        country: data?.firstPickUp?.country ?? ""
      };
      const destinationLocations = {
        streetOne: data?.finalDelivery?.address ?? data?.finalDelivery?.street_one,
        city: data?.finalDelivery?.city ?? "",
        state: data?.finalDelivery?.state ?? "",
        zip: data?.finalDelivery?.zip ?? "",
        country: data?.finalDelivery?.country ?? ""
      };

      // Mark as Delivered on the assigned-loads flow. HP-8994: a carrier's own funding request is
      // never transitioned by saving. It moves only through submitToDocumentReview below.
      if (isAssignedLoadsPage && hasBOLDocument) {
        if (["Incomplete", "Document Issue"].includes(selectedFR?.status)) {
          data.fundingRequestStatus = {
            ...data.fundingRequestStatus,
            id: data.fundingRequestStatus?.id || -1,
            value: "Pending",
            label: "Pending"
          };
          showSuccessMessage("✅ Funding request marked as complete and submitted for processing.");
          markedAsCompleted = true;
          setIsMarkedComplete(markedAsCompleted);
        }
      }
      if (isBroker) {
        const frLineList =
          (data?.lineItems ?? []).map((item) => ({
            id: item.id ?? undefined,
            amount: item.amount ? dollarsToCents(item.amount) : 0,
            description: item.description,
            category: item.category.value
          })) ?? [];

        let metadata = {};
        const isBillOutSource =
          selectedFR?.contractType === FUNDING_REQUEST_CONTRACT_TYPES.NON_FACTORED_BILL_OUT;
        if (
          isNonFactorRefactorEnabled &&
          selectedFR?.metadata?.previous_payout_days &&
          !isBillOutSource &&
          (([FUNDING_REQUEST_CONTRACT_TYPES.NON_FACTORED_STANDARD].includes(
            selectedFR.contractType
          ) &&
            data.fundingRequestType?.value === FUNDING_REQUEST_CONTRACT_TYPES.STD_BROKER) ||
            (data?.fundingRequestType &&
              [FUNDING_REQUEST_CONTRACT_TYPES.NON_FACTORED_STANDARD].includes(
                data?.fundingRequestType?.value
              ) &&
              selectedFR.contractType === FUNDING_REQUEST_CONTRACT_TYPES.STD_BROKER))
        ) {
          metadata = {
            paymentProfileId: selectedFR?.metadata?.previous_payment_profile_id,
            factoringFeeSplitRate: selectedFR?.metadata?.previous_discount_rate,
            paymentProfileAmount: selectedFR?.metadata?.previous_payment_profile_amount,
            payoutDays:
              selectedFR?.metadata?.previous_payout_days === 0
                ? "ach_1_day"
                : `ach_${selectedFR?.metadata?.previous_payout_days}_day`
          };
        }

        const variables = {
          id: fundingRequestId,
          amount: dollarsToCents(data.shipperInvoiceAmount),
          applyReserveHoldback: data.marginAdvance,
          debtor: data.debtor?.id,
          paymentProfileAmount: dollarsToCents(data.carrierInvoiceAmount),
          userLoadNumber: data.userLoadNumber,
          loadLength: selectedFR?.loadLength,
          paymentProfilePaymentMethod: (data?.paymentProfileDisplay || "ach").toLowerCase(),
          paymentProfileId: selectedPaymentProfile?.id ?? selectedFR?.paymentProfile?.id,
          factoringFeeSplitRate: data.brokersQRFee?.value,
          payoutDays: data.carrierPaymentSpeed?.value,
          lineItems: frLineList,
          contractType: data.fundingRequestType?.value,
          carrierProNumber: data.carrierPro,
          purchaseOrderNumber: data.PONumber,
          status: data.fundingRequestStatus?.value,
          originLocations,
          destinationLocations,
          attachments:
            data.attachments?.map((att) => {
              const normalizeCategoryValue = (val: string): string => {
                const raw = String(val ?? "").trim();
                if (!raw) return "";
                const match = UPLOAD_CATEGORY.find(
                  (opt) => String(opt.value).toLowerCase() === raw.toLowerCase()
                );
                return (match?.value as string) || raw;
              };
              const normalizeCategory = (category: any): string => {
                if (Array.isArray(category)) return category.map(normalizeCategoryValue).join(",");
                return normalizeCategoryValue(String(category ?? ""));
              };
              return {
                visibleTo: att.visibleTo,
                filename: att.filename,
                id: att.id,
                category: normalizeCategory(att.category)
              };
            }) ?? [],
          userNotes: data.userNotes,
          userReferenceNumber: data.clientInvoiceNumber,
          debtorInvoiceNotes: data.debtorInvoiceDetails,
          carrierInvoiceNotes: data.carrierInvoiceDetails,
          statusNote: data.statusNote,
          ...metadata
        };

        const { attachments, debtorInvoiceNotes, carrierInvoiceNotes, status, contractType } =
          variables as any;

        const isConvertingFromSelfFinanceNoInvoiceToNonFactored =
          selectedFR?.contractType === FUNDING_REQUEST_CONTRACT_TYPES.SELF_FINANCE_NO_INVOICE &&
          (contractType === FUNDING_REQUEST_CONTRACT_TYPES.NON_FACTORED_STANDARD ||
            contractType === FUNDING_REQUEST_CONTRACT_TYPES.NON_FACTORED_BILL_OUT);

        if (
          isConvertingFromSelfFinanceNoInvoiceToNonFactored &&
          data?.debtor?.id === NO_DEBTOR_ID
        ) {
          paymentSettingsConfirmationAbortControllerRef.current?.abort();

          const paymentSettingsConfirmationAbortController = new AbortController();
          paymentSettingsConfirmationAbortControllerRef.current =
            paymentSettingsConfirmationAbortController;

          const selectedDebtor = await confirmSelfFinanceNoInvoiceToNonFactored({
            openModal,
            closeModal,
            fromContractType: selectedFR?.contractType,
            toContractType: contractType,
            signal: paymentSettingsConfirmationAbortController.signal
          }).finally(() => {
            if (
              paymentSettingsConfirmationAbortControllerRef.current ===
              paymentSettingsConfirmationAbortController
            ) {
              paymentSettingsConfirmationAbortControllerRef.current = null;
            }
          });

          variables.debtor = selectedDebtor.id;
          setValue("debtor", selectedDebtor);
        }

        const isNonFactoredToFactoredTransition =
          selectedFR?.contractType === FUNDING_REQUEST_CONTRACT_TYPES.NON_FACTORED_BILL_OUT &&
          [
            FUNDING_REQUEST_CONTRACT_TYPES.STD_BROKER,
            FUNDING_REQUEST_CONTRACT_TYPES.SELF_FINANCE_WITH_INVOICE,
            FUNDING_REQUEST_CONTRACT_TYPES.SELF_FINANCE_NO_INVOICE
          ].includes(contractType);

        let localPaymentProfile: IPaymentProfile | undefined = selectedPaymentProfile;
        let localCarrierPaymentSpeed = data?.carrierPaymentSpeed;
        let localBrokersQRFee = data?.brokersQRFee;
        const localHasFactoringCompany = hasFactoringCompany;

        if (isNonFactoredToFactoredTransition) {
          paymentSettingsConfirmationAbortControllerRef.current?.abort();

          const paymentSettingsConfirmationAbortController = new AbortController();
          paymentSettingsConfirmationAbortControllerRef.current =
            paymentSettingsConfirmationAbortController;

          const previousMetadata =
            isNonFactorRefactorEnabled && selectedFR?.metadata?.previous_payment_profile_id
              ? {
                  previousPaymentProfileId: selectedFR.metadata.previous_payment_profile_id,
                  previousPaymentProfileAmount: selectedFR.metadata.previous_payment_profile_amount,
                  previousDiscountRate: selectedFR.metadata.previous_discount_rate,
                  previousFactoringFeeSplitRate:
                    selectedFR.metadata.previous_factoring_fee_split_rate,
                  previousPayoutDays: selectedFR.metadata.previous_payout_days
                }
              : undefined;

          const result = await confirmNonFactoredToFactored({
            openModal,
            closeModal,
            fromContractType: selectedFR?.contractType,
            toContractType: contractType,
            previousMetadata,
            signal: paymentSettingsConfirmationAbortController.signal
          }).finally(() => {
            if (
              paymentSettingsConfirmationAbortControllerRef.current ===
              paymentSettingsConfirmationAbortController
            ) {
              paymentSettingsConfirmationAbortControllerRef.current = null;
            }
          });

          localPaymentProfile = result.carrier as IPaymentProfile;
          localCarrierPaymentSpeed = result.carrierPaymentSpeed;
          localBrokersQRFee = result.brokersQRFee;
          setSelectedPaymentProfile(localPaymentProfile);
          setValue("carrierPaymentSpeed", localCarrierPaymentSpeed, { shouldDirty: true });
          setValue("brokersQRFee", localBrokersQRFee, { shouldDirty: true });
          setValue("carrierInvoiceAmount", result.carrierInvoiceAmount, { shouldDirty: true });
          variables.paymentProfileId = localPaymentProfile.id;
          variables.paymentProfileAmount = dollarsToCents(result.carrierInvoiceAmount);
          variables.payoutDays = localCarrierPaymentSpeed.value;
          variables.factoringFeeSplitRate = localBrokersQRFee.value;
        }

        let selectedCarrierPaymentSpeed = localCarrierPaymentSpeed;
        let selectedBrokersQRFee = localHasFactoringCompany
          ? LOCKED_BROKER_SPLIT_OPTION
          : localBrokersQRFee;

        const relationPayoutDays =
          (localPaymentProfile as any)?.brokerPaymentProfileRelation?.payoutDays ??
          selectedFR?.paymentProfile?.brokerPaymentProfileRelation?.payoutDays;
        const relationFactoringFeeSplitRate =
          (localPaymentProfile as any)?.brokerPaymentProfileRelation?.factoringFeeSplitRate ??
          selectedFR?.paymentProfile?.brokerPaymentProfileRelation?.factoringFeeSplitRate;
        const initialFormValues = initialValuesRef.current;
        const offPlatformCarrierAmountChanged =
          data.carrierInvoiceAmount !== initialFormValues?.carrierInvoiceAmount;
        const offPlatformShipperAmountChanged =
          data.shipperInvoiceAmount !== initialFormValues?.shipperInvoiceAmount;
        const offPlatformPayoutDaysChanged =
          data.carrierPaymentSpeed?.value !== initialFormValues?.carrierPaymentSpeed?.value;
        const offPlatformBrokerSplitChanged =
          data.brokersQRFee?.value !== initialFormValues?.brokersQRFee?.value;
        const isSelfFinanceOffPlatformEdit = isApprovedSelfFinanceOffPlatformEdit({
          contractType: selectedFR?.contractType,
          status: selectedFR?.status,
          carrierAmountChanged: offPlatformCarrierAmountChanged,
          shipperAmountChanged: offPlatformShipperAmountChanged,
          payoutDaysChanged: offPlatformPayoutDaysChanged,
          brokerSplitChanged: offPlatformBrokerSplitChanged
        });
        // HP-9057 / HP-9058: "Paid" is inside the helper's status gate so a shipper amount
        // correction made after the carrier is paid still offers the resend, and PO # / Client
        // Invoice # get the same treatment — all three are printed on the invoice the backend
        // regenerates. Carrier-side edits cannot reach here on a Paid FR; those inputs stay
        // disabled by permissions.ts.
        resendInvoicePrompt = getOffPlatformResendInvoicePrompt({
          contractType: selectedFR?.contractType,
          status: selectedFR?.status,
          shipperAmountChanged: offPlatformShipperAmountChanged,
          poNumberChanged: data.PONumber !== initialFormValues?.PONumber,
          clientInvoiceNumberChanged:
            data.clientInvoiceNumber !== initialFormValues?.clientInvoiceNumber
        });

        const paymentSettingsConfirmationModalId = getPaymentSettingsConfirmationModalId({
          previousContractType: selectedFR?.contractType,
          nextContractType: contractType,
          hasPaymentProfile: Boolean(localPaymentProfile || data?.paymentProfile),
          fundingRequestStatus: data?.fundingRequestStatus?.value,
          fundingRequestType: data?.fundingRequestType?.value,
          hasPaymentSpeedMismatch: relationPayoutDays !== data?.carrierPaymentSpeed?.value,
          hasBrokerSplitMismatch:
            Number(relationFactoringFeeSplitRate) !== Number(data?.brokersQRFee?.value),
          isSelfFinanceOffPlatformEdit
        });

        if (paymentSettingsConfirmationModalId === "confirmation-fr-payment-speed") {
          await new Promise((resolve, reject) =>
            openModal("confirmation-fr-payment-speed", {
              confirmButton: () => {
                resolve(true);
                closeModal("confirmation-fr-payment-speed");
              },
              onCloseModal: () => reject("cancel-edit-fr"),
              defaultValue: {
                payout_days: relationPayoutDays,
                factoring_fee_split_rate: relationFactoringFeeSplitRate
              },
              currentValue: {
                payout_days: data?.carrierPaymentSpeed?.value,
                factoring_fee_split_rate: data?.brokersQRFee?.value
              },
              initialValue: {
                factoring_fee_split_rate: initialValuesRef.current?.brokersQRFee?.value,
                payout_days: initialValuesRef.current?.carrierPaymentSpeed?.value
              }
            })
          );
        }

        if (paymentSettingsConfirmationModalId === "contract-type-change-confirmation") {
          paymentSettingsConfirmationAbortControllerRef.current?.abort();

          const paymentSettingsConfirmationAbortController = new AbortController();
          paymentSettingsConfirmationAbortControllerRef.current =
            paymentSettingsConfirmationAbortController;

          const confirmedPaymentSettings = await confirmContractTypeChangePaymentSettings({
            openModal,
            closeModal,
            currentValue: {
              payout_days: selectedFR?.payoutDays,
              factoring_fee_split_rate: selectedFR?.factoringFeeSplitRate
            },
            defaultValue: {
              payout_days: relationPayoutDays,
              factoring_fee_split_rate: relationFactoringFeeSplitRate
            },
            initialValue: {
              carrierPaymentSpeed: selectedCarrierPaymentSpeed,
              brokersQRFee: selectedBrokersQRFee
            },
            hasFactoringCompany: localHasFactoringCompany,
            factoringId: selectedFR?.factoring?.id,
            fromContractType:
              FUNDING_REQUEST_CONTRACT_TYPES_MAP[
                selectedFR?.contractType as keyof typeof FUNDING_REQUEST_CONTRACT_TYPES_MAP
              ],
            toContractType:
              FUNDING_REQUEST_CONTRACT_TYPES_MAP[
                contractType as keyof typeof FUNDING_REQUEST_CONTRACT_TYPES_MAP
              ],
            signal: paymentSettingsConfirmationAbortController.signal
          }).finally(() => {
            if (
              paymentSettingsConfirmationAbortControllerRef.current ===
              paymentSettingsConfirmationAbortController
            ) {
              paymentSettingsConfirmationAbortControllerRef.current = null;
            }
          });

          selectedCarrierPaymentSpeed = confirmedPaymentSettings.carrierPaymentSpeed;
          selectedBrokersQRFee = localHasFactoringCompany
            ? LOCKED_BROKER_SPLIT_OPTION
            : confirmedPaymentSettings.brokersQRFee;

          setValue("carrierPaymentSpeed", selectedCarrierPaymentSpeed, { shouldDirty: true });
          setValue("brokersQRFee", selectedBrokersQRFee, { shouldDirty: true });
          variables.payoutDays = selectedCarrierPaymentSpeed?.value;
          variables.factoringFeeSplitRate = selectedBrokersQRFee?.value;
        }

        if (paymentSettingsConfirmationModalId === "self-finance-no-invoice-to-factored") {
          paymentSettingsConfirmationAbortControllerRef.current?.abort();

          const paymentSettingsConfirmationAbortController = new AbortController();
          paymentSettingsConfirmationAbortControllerRef.current =
            paymentSettingsConfirmationAbortController;

          const result = await confirmSelfFinanceNoInvoiceToFactored({
            openModal,
            closeModal,
            fromContractType: selectedFR?.contractType,
            toContractType: contractType,
            hasFactoringCompany: localHasFactoringCompany,
            initialPaymentSpeed: selectedCarrierPaymentSpeed,
            initialBrokerSplit: selectedBrokersQRFee,
            signal: paymentSettingsConfirmationAbortController.signal
          }).finally(() => {
            if (
              paymentSettingsConfirmationAbortControllerRef.current ===
              paymentSettingsConfirmationAbortController
            ) {
              paymentSettingsConfirmationAbortControllerRef.current = null;
            }
          });

          variables.debtor = result.debtor.id;
          setValue("debtor", result.debtor);

          selectedCarrierPaymentSpeed = result.carrierPaymentSpeed;
          selectedBrokersQRFee = localHasFactoringCompany
            ? LOCKED_BROKER_SPLIT_OPTION
            : result.brokersQRFee;

          setValue("carrierPaymentSpeed", selectedCarrierPaymentSpeed, { shouldDirty: true });
          setValue("brokersQRFee", selectedBrokersQRFee, { shouldDirty: true });
          variables.payoutDays = selectedCarrierPaymentSpeed?.value;
          variables.factoringFeeSplitRate = selectedBrokersQRFee?.value;
        }

        if (
          paymentSettingsConfirmationModalId ===
          "self-finance-no-invoice-to-self-finance-with-invoice"
        ) {
          paymentSettingsConfirmationAbortControllerRef.current?.abort();

          const paymentSettingsConfirmationAbortController = new AbortController();
          paymentSettingsConfirmationAbortControllerRef.current =
            paymentSettingsConfirmationAbortController;

          const result = await confirmSelfFinanceNoInvoiceToSelfFinanceWithInvoice({
            openModal,
            closeModal,
            fromContractType: selectedFR?.contractType,
            toContractType: contractType,
            hasFactoringCompany: localHasFactoringCompany,
            initialPaymentSpeed: selectedCarrierPaymentSpeed,
            initialBrokerSplit: selectedBrokersQRFee,
            signal: paymentSettingsConfirmationAbortController.signal
          }).finally(() => {
            if (
              paymentSettingsConfirmationAbortControllerRef.current ===
              paymentSettingsConfirmationAbortController
            ) {
              paymentSettingsConfirmationAbortControllerRef.current = null;
            }
          });

          variables.debtor = result.debtor.id;
          setValue("debtor", result.debtor);

          selectedCarrierPaymentSpeed = result.carrierPaymentSpeed;
          selectedBrokersQRFee = localHasFactoringCompany
            ? LOCKED_BROKER_SPLIT_OPTION
            : result.brokersQRFee;

          setValue("carrierPaymentSpeed", selectedCarrierPaymentSpeed, { shouldDirty: true });
          setValue("brokersQRFee", selectedBrokersQRFee, { shouldDirty: true });
          variables.payoutDays = selectedCarrierPaymentSpeed?.value;
          variables.factoringFeeSplitRate = selectedBrokersQRFee?.value;
        }

        if (paymentSettingsConfirmationModalId === "non-factored-bill-out-to-pay-carrier") {
          paymentSettingsConfirmationAbortControllerRef.current?.abort();

          const paymentSettingsConfirmationAbortController = new AbortController();
          paymentSettingsConfirmationAbortControllerRef.current =
            paymentSettingsConfirmationAbortController;

          const billOutPreviousMetadata =
            isNonFactorRefactorEnabled && selectedFR?.metadata?.previous_payment_profile_id
              ? {
                  previousPaymentProfileId: selectedFR.metadata.previous_payment_profile_id,
                  previousPaymentProfileAmount: selectedFR.metadata.previous_payment_profile_amount,
                  previousDiscountRate: selectedFR.metadata.previous_discount_rate,
                  previousFactoringFeeSplitRate:
                    selectedFR.metadata.previous_factoring_fee_split_rate,
                  previousPayoutDays: selectedFR.metadata.previous_payout_days
                }
              : undefined;

          const result = await confirmNonFactoredBillOutToPayCarrier({
            openModal,
            closeModal,
            fromContractType: selectedFR?.contractType,
            toContractType: contractType,
            previousMetadata: billOutPreviousMetadata,
            signal: paymentSettingsConfirmationAbortController.signal
          }).finally(() => {
            if (
              paymentSettingsConfirmationAbortControllerRef.current ===
              paymentSettingsConfirmationAbortController
            ) {
              paymentSettingsConfirmationAbortControllerRef.current = null;
            }
          });

          localPaymentProfile = result.carrier as IPaymentProfile;
          setSelectedPaymentProfile(localPaymentProfile);
          setValue("carrierInvoiceAmount", result.carrierInvoiceAmount, { shouldDirty: true });
          variables.paymentProfileId = localPaymentProfile.id;
          variables.paymentProfileAmount = dollarsToCents(result.carrierInvoiceAmount);
        }

        if (paymentSettingsConfirmationModalId === "self-finance-off-platform-edit") {
          const changes: OffPlatformEditSummaryRow[] = [];
          if (offPlatformCarrierAmountChanged) {
            changes.push({
              label: "Carrier invoice amount",
              from: formatNumber(initialFormValues?.carrierInvoiceAmount ?? 0, 2),
              to: formatNumber(data.carrierInvoiceAmount ?? 0, 2)
            });
          }
          if (offPlatformPayoutDaysChanged) {
            changes.push({
              label: "Payment speed",
              from:
                initialFormValues?.carrierPaymentSpeed?.label ??
                getPaymentSpeedSummary(initialFormValues?.carrierPaymentSpeed?.value),
              to:
                data.carrierPaymentSpeed?.label ??
                getPaymentSpeedSummary(data.carrierPaymentSpeed?.value)
            });
          }
          if (offPlatformBrokerSplitChanged) {
            changes.push({
              label: "Broker split",
              from:
                initialFormValues?.brokersQRFee?.label ??
                `${initialFormValues?.brokersQRFee?.value ?? 0}%`,
              to: data.brokersQRFee?.label ?? `${data.brokersQRFee?.value ?? 0}%`
            });
          }
          if (offPlatformShipperAmountChanged) {
            changes.push({
              label: "Shipper invoice amount",
              from: formatNumber(initialFormValues?.shipperInvoiceAmount ?? 0, 2),
              to: formatNumber(data.shipperInvoiceAmount ?? 0, 2)
            });
          }

          // Pull fresh rates on demand so the preview reflects the latest edits regardless of
          // the 500ms debounced calculation (calculationResults can be stale on a fast submit).
          // Rate amounts come back in cents; convert to dollars for display.
          let recomputed: OffPlatformEditSummaryRow[] = [];
          try {
            const offPlatformContractType =
              data.fundingRequestType?.value ?? selectedFR?.contractType;
            const payoutDaysRaw = data.carrierPaymentSpeed?.value ?? data.carrierPaymentSpeed;
            const payoutDaysMatch = String(payoutDaysRaw ?? "").match(/\d+/g);
            const paymentSpeed = payoutDaysMatch?.length ? parseInt(payoutDaysMatch[0], 10) : 0;
            const freshRates = await calculateFR({
              debtor: selectedFR?.debtor?.id,
              factored: !String(offPlatformContractType ?? "").startsWith("NON_FACTORED"),
              invoice_amount: dollarsToCents(data.shipperInvoiceAmount),
              bill_out_only: false,
              funding_request_id: selectedFR?.id,
              contract_type: offPlatformContractType,
              discount_rate_comfreight: selectedFR?.discountRate,
              apply_reserve_holdback: Boolean(data.marginAdvance),
              factoring: selectedFR?.factoring?.id,
              create_rates_revision: false,
              factoring_fee_split_rate: Number(data.brokersQRFee?.value),
              self_finance_requested: true,
              payment_profile: selectedFR?.paymentProfile?.id,
              payment_profile_amount: 0,
              payment_profile_invoice_amount: dollarsToCents(data.carrierInvoiceAmount),
              payment_speed: paymentSpeed
            });
            const rates = freshRates?.data;
            if (rates) {
              recomputed = [
                {
                  label: "Carrier advance",
                  value: formatNumber(Number(rates.broker_carrier_advance ?? 0) / 100, 2)
                },
                {
                  label: rates.self_finance_fee_rate_info
                    ? `Self-finance fee (${rates.self_finance_fee_rate_info})`
                    : "Self-finance fee",
                  value: formatNumber(Number(rates.broker_self_finance_fee ?? 0) / 100, 2)
                },
                {
                  label: "Carrier pay",
                  value: formatNumber(Number(rates.payment_profile_payable ?? 0) / 100, 2)
                }
              ];
            }
          } catch {
            recomputed = [];
          }

          paymentSettingsConfirmationAbortControllerRef.current?.abort();

          const paymentSettingsConfirmationAbortController = new AbortController();
          paymentSettingsConfirmationAbortControllerRef.current =
            paymentSettingsConfirmationAbortController;

          await confirmSelfFinanceOffPlatformEdit({
            openModal,
            closeModal,
            changes,
            recomputed,
            amountChanged: offPlatformShipperAmountChanged,
            payoutDaysChanged: offPlatformPayoutDaysChanged,
            signal: paymentSettingsConfirmationAbortController.signal
          }).finally(() => {
            if (
              paymentSettingsConfirmationAbortControllerRef.current ===
              paymentSettingsConfirmationAbortController
            ) {
              paymentSettingsConfirmationAbortControllerRef.current = null;
            }
          });
        }

        const isNonFactoredFR = [
          FUNDING_REQUEST_CONTRACT_TYPES.NON_FACTORED_STANDARD,
          FUNDING_REQUEST_CONTRACT_TYPES.NON_FACTORED_BILL_OUT
        ].includes(contractType);

        if (
          shouldOmitStatusFromBrokerUpdate(selectedFR?.status) ||
          canBrokerEditApprovedFactoredFR({
            isBroker,
            status: selectedFR?.status,
            debtorName: selectedFR?.debtor?.name ?? null,
            isNonFactoredFR
          })
        ) {
          // If broker is editing an approved or paid FR, do not update the status
          delete variables.status;
        }

        const validationRequest = {
          ...Object.fromEntries(
            Object.entries(variables).filter(
              ([k]) =>
                ![
                  "attachments",
                  "debtorInvoiceNotes",
                  "carrierInvoiceNotes",
                  "status",
                  "contractType"
                ].includes(k)
            )
          ),
          files: attachments || [],
          debtorInvoiceNotes: debtorInvoiceNotes || "",
          carrierInvoiceNotes: carrierInvoiceNotes || "",
          originLocations: [originLocations],
          destinationLocations: [destinationLocations],
          debtor: variables.debtor ?? data?.debtor?.id
        };

        const validationResponse = await validateFRs({
          variables: {
            request: validationRequest,
            contractType,
            status: status.toUpperCase().replace(/\s+/g, "_")
          }
        });
        if (validationResponse?.errors?.length) return false;
        const hasWarnings = validationResponse.data.validateFundingRequests.results.some(
          (r: any) => r?.warnings?.length > 0
        );
        if (hasWarnings) {
          const warnings = validationResponse.data.validateFundingRequests.results.flatMap(
            (r: any) => r.warnings.map((warning: string) => warning)
          );
          for (const warning of warnings) {
            await new Promise((resolve, reject) =>
              openModal("warning", {
                message: warning,
                onConfirm: () => {
                  resolve(true);
                  closeModal("warning");
                },
                onClose: () => {
                  reject("cancel-edit-fr");
                  closeModal("warning");
                },
                confirmText: "Submit",
                closeText: "Close"
              })
            );
          }
        }

        res = await updateBrokerFR({ variables });
      } else {
        const variables: any = {
          id: fundingRequestId,
          amount: dollarsToCents(data?.shipperInvoiceAmount),
          contractType: data?.fundingRequestType?.value,
          ...(selectedFR?.status === "Incomplete"
            ? { debtorAdvanceAmount: dollarsToCents(data?.debtorAdvanceAmount) }
            : {}),
          userLoadNumber: data.userLoadNumber ?? "",
          billToCompanyPhone: data.billToCompanyPhone ?? "",
          streetOne: data?.billToCompanyAddress?.address ?? data?.billToCompanyAddress?.street_one,
          city: data?.billToCompanyAddress?.city ?? "",
          state: data?.billToCompanyAddress?.state ?? "",
          zip: data?.billToCompanyAddress?.zip ?? "",
          country: data?.billToCompanyAddress?.country ?? "",
          // fuelAdvanceAmount: Number((data.shipperInvoiceAmount ?? 0) * 100),
          originLocations,
          destinationLocations,
          autoInvoice: data?.fundingRequestType?.value === "NON_FACTORED_BILL_OUT",
          // HP-8994: only the assigned-loads Mark as Delivered flow may carry a status. A carrier
          // saving their own request never sends one, so Save Updates cannot change the status.
          ...(selectedFR?.status === "Incomplete" && isAssignedLoadsPage
            ? { status: data.fundingRequestStatus?.value }
            : {}),
          markedAsCompleted: markedAsCompleted,
          statusNote: data.statusNote ?? ""
        };

        // Add debtor conditionally for non-assigned loads
        if (!isAssignedLoadsPage) {
          variables.debtor = data?.debtor?.id ?? "";
        }

        res = await updateCarrierFR({ variables });
      }

      if (!res?.errors) {
        await refetch();
        const warning =
          res?.data?.createOrUpdateBrokerFundingRequest?.fundingRequest?.warnings?.negative_margin;
        if (warning) {
          showWarningMessage(warning);
        }
        showSuccessMessage("Successfully edited transaction");

        if (resendInvoicePrompt) {
          openModal("info", {
            title: "Resend invoice?",
            message: resendInvoicePrompt,
            confirmText: "Resend invoice",
            closeText: "Not now",
            onConfirm: async () => {
              closeModal("info");
              await sendInvoice();
            },
            onClose: () => closeModal("info")
          });
        }

        return true;
      }
    } catch (e) {
      if (e === "cancel-edit-fr") {
        throw e;
      }
      if (e instanceof Error && e.name === "AbortError") {
        return false;
      }
      showErrorMessage("Failed to edit transaction");
    }
    return false;
  };

  // HP-8994: the only path that moves a carrier's FR to Document Review. The backend re-validates
  // the document labels, so a stale or bypassed UI still cannot submit.
  const submitToDocumentReview = async (): Promise<boolean> => {
    if (!fundingRequestId) return false;
    setIsSubmittingToDocumentReview(true);
    documentReviewErrorShownRef.current = false;
    try {
      const res = await submitFRToDocumentReview({ variables: { id: fundingRequestId } });
      if (!res?.data?.submitFundingRequestToDocumentReview?.success) {
        // onError already surfaced the specific backend reason; only fall back when it did not.
        if (!documentReviewErrorShownRef.current) {
          showErrorMessage("Failed to submit the funding request to Document Review.");
        }
        return false;
      }

      showSuccessMessage("Funding request submitted to Document Review.");
      if (autoInvoice) {
        showSuccessMessage("Automated invoicing is enabled for this request.");
      }
      await refetch();
      return true;
    } catch {
      showErrorMessage("Failed to submit the funding request to Document Review.");
      return false;
    } finally {
      setIsSubmittingToDocumentReview(false);
    }
  };

  // Helper function to check if attachments contain BOL document
  // Category name must match backend: common/constants.py FundingRequestAttachmentCategories
  // HP-8994: an attachment row exists from the moment an upload slot is requested, so a delivered
  // BOL only counts once it has finished uploading, matching has_uploaded_delivered_bol.
  const checkForBOLDocument = (attachments: IFRAttachment[]) => {
    return attachments.some((att: Partial<IFRAttachment>) => {
      if (!att?.category || !att?.uploaded) return false;
      let categories: any = att.category;
      if (Array.isArray(categories)) categories = categories.join(",");
      const categoryStr = String(categories).toLowerCase();
      return categoryStr.includes("signed bill of lading (delivered)");
    });
  };

  // HP-8994: the Save / Mark as Complete pair replaces the old single Update button for a carrier's
  // own funding request. It stays visible in Document Review so the carrier sees the completed,
  // locked state instead of a bare disabled Update. The assigned-loads flow keeps Mark as Delivered.
  const isMarkedCompleteState = isCarrier && selectedFR?.status === "Document Review";
  const showCarrierSubmitActions =
    isCarrier &&
    !isAssignedLoadsPage &&
    ["Incomplete", "Document Issue", "Document Review"].includes(selectedFR?.status ?? "");
  // Derived from the live attachments list rather than selectedFR.canSubmitToDocumentReview so the
  // button reacts the moment a document finishes uploading or is relabeled, without waiting for a
  // refetch. It applies the same rule as the backend, which re-validates on submit either way.
  // HP-9251: a fuel advance is paid against pickup, so the backend accepts the pickup BOL in place
  // of the delivered one. The gate has to agree or the carrier cannot reach the submission the
  // backend would allow.
  const isFuelAdvance = Boolean(selectedFR?.fuelAdvanceAmount);
  const canSubmitToDocumentReview = hasRequiredDocumentReviewDocuments(attachments, isFuelAdvance);

  // Attachments
  const handleAttachmentsChange = (newAttachments: IFRAttachment[]) => {
    const hasBOL = checkForBOLDocument(newAttachments);
    setHasBOLDocument(hasBOL);
    setAttachments(newAttachments);
    setValue("attachments", newAttachments as any);
  };

  return (
    <FundingRequestFormContext.Provider
      value={{
        methods,
        selectedFR,
        attachments,
        isGlobalDisabled,
        allowDocsUpdate,
        isSubmitting,
        loading,
        isLockedForReview,
        rhfHandleSubmit: methods.handleSubmit,
        submitFR,
        submitToDocumentReview,
        canSubmitToDocumentReview,
        isFuelAdvance,
        showCarrierSubmitActions,
        isMarkedCompleteState,
        isSubmittingToDocumentReview,
        sendInvoice,
        resendRateConfirmation,
        deleteFR,
        declineFR,
        cancelCarrierPayment,
        handleAttachmentsChange,
        isMarkedComplete,
        hasBOLDocument,
        isAssignedLoadsPage,
        brokerGrossMargin,
        reserveHoldBack,
        factoringFee,
        factoringPayable,
        isFetchingCalculation,
        brokersQPFee,
        brokerPay,
        carrierRate: carrierRate || "0%",
        carrierFee,
        yourFee,
        carrierPay,
        setSelectedPaymentProfile,
        selectedPaymentProfile,
        refetch,
        setIsAttachmentsListIsModalOpen,
        isAttachmentListModalOpen,
        editingAttachment,
        setEditingAttachment,
        formMethods: methods,
        selfFinanceFeeRateInfo,
        selfFinanceFee,
        brokerCarrierAdvance,
        selfFinanceTotalAmount,
        qpFees,
        brokerQPProfit,
        hasFactoringCompany,
        debtorAdvanceAmount,
        debtorAdditionalDiscountRate,
        brokerAdditionalDiscountFeeForCarrier,
        brokerAdditionalDiscountFeeForBroker,
        marginAdvanceFee,
        marginAdvanceAmount,
        brokerAdvanceFeePercent,
        brokerDiscountFeeForCarrier,
        isTypeChanged,
        isOtherFieldsChanged,
        isCarrierFieldsChanged,
        isSelfFinanceOffPlatformEditPending,
        isTypeDisabled,
        isOtherFieldsDisabled,
        typeDisabledTooltip,
        otherFieldsDisabledTooltip,
        activeTooltipId,
        setActiveTooltipId
      }}>
      <FormProvider {...methods}>{children}</FormProvider>
    </FundingRequestFormContext.Provider>
  );
}

// ===== Hook =====
export function useFundingRequestForm() {
  const ctx = useContext(FundingRequestFormContext);
  if (!ctx)
    throw new Error("useFundingRequestForm must be used within a FundingRequestFormProvider");
  return ctx;
}
